import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { archiveByKey, archiveLayerMap, type Archive } from '../../api/archives';
import type { BinEntry } from '../../api/backend';
import {
  annotationItemId,
  findAnnotations,
  findItemsAdvanced,
  findItemsCount,
  getItem,
  getSiteInfo,
  type PandoraAnnotation,
  type PandoraCondition,
  type PandoraItem,
  type PandoraItemKey,
  type PandoraLayer,
  type PandoraSort,
} from '../../api/pandora';
import { buildQuery, emptyGroup, emptyRow, type ConditionGroup } from '../../api/pandoraQuery';
import { useKeyBindings, useKeyScope } from '../../keys/useKeyScope';
import ConditionRowEditor from './ConditionRowEditor';
import FacetSidebar from './FacetSidebar';
import ResultGrid from './ResultGrid';
import './AdvancedSearchOverlay.css';

const PAGE = 40;

// itemKeys fallback when `init` is unreachable
const FALLBACK_KEYS: PandoraItemKey[] = ['*', 'title', 'director', 'year', 'date', 'language', 'topic'].map(
  (id) => ({ id, type: 'string' }),
);

const FACET_KEY_CANDIDATES = [
  'director',
  'year',
  'language',
  'topic',
  'keywords',
  'featuring',
  'country',
  'date',
  'source',
  'project',
];

const SORT_KEYS = ['modified', 'title', 'year', 'duration'] as const;

type Mode = 'items' | 'moments';

export interface AdvancedSearchProps {
  archives: Archive[];
  onAddToBin(entry: Omit<BinEntry, 'id' | 'addedAt'>): void;
  onOpenItem(
    archiveKey: string,
    itemId: string,
    preset?: { concatIn: number; concatOut: number },
  ): void;
  onClose(): void;
}

/**
 * Full-screen advanced search: condition-group query builder + facet sidebar +
 * results grid. Window-agnostic — talks to the editor only via the callbacks.
 */
export default function AdvancedSearchOverlay({
  archives,
  onAddToBin,
  onOpenItem,
  onClose,
}: AdvancedSearchProps) {
  const [archiveKey, setArchiveKey] = useState(archives[0]?.key ?? 'pad.ma');
  const [mode, setMode] = useState<Mode>('items');
  const [group, setGroup] = useState<ConditionGroup>(() => ({
    ...emptyGroup(),
    rows: [emptyRow()],
  }));
  const [momentQuery, setMomentQuery] = useState('');
  const [layerId, setLayerId] = useState('');
  const [sortKey, setSortKey] = useState<string>('modified');
  const [sortDir, setSortDir] = useState<'+' | '-'>('-');
  const [streamOnly, setStreamOnly] = useState(false);
  const [itemKeys, setItemKeys] = useState<PandoraItemKey[]>(FALLBACK_KEYS);
  const [layers, setLayers] = useState<PandoraLayer[]>([]);
  const [facetKey, setFacetKey] = useState('director');
  const [items, setItems] = useState<PandoraItem[]>([]);
  const [moments, setMoments] = useState<PandoraAnnotation[]>([]);
  const [count, setCount] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [exhausted, setExhausted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [binnedId, setBinnedId] = useState<string | null>(null);
  const generation = useRef(0);

  const archive = archiveByKey(archives, archiveKey) ?? archives[0];

  // Esc closes (search-overlay scope shadows global bindings while mounted)
  useKeyScope('search-overlay');
  useKeyBindings(
    () => [
      {
        id: 'advancedSearch.close',
        keys: ['Escape'],
        scope: 'search-overlay',
        group: 'Search',
        description: 'Close advanced search',
        allowInInputs: true,
        run: onClose,
      },
    ],
    [onClose],
  );

  // per-archive key + layer lists from `init`, with hardcoded fallbacks
  useEffect(() => {
    if (!archive) return;
    let alive = true;
    getSiteInfo(archive.site).then(
      (info) => {
        if (!alive) return;
        setItemKeys(info.itemKeys.length ? info.itemKeys : FALLBACK_KEYS);
        setLayers(
          info.layers.length
            ? info.layers
            : archiveLayerMap(archive.key).layers.map((id) => ({ id, title: id })),
        );
      },
      () => {
        if (!alive) return;
        setItemKeys(FALLBACK_KEYS);
        setLayers(archiveLayerMap(archive.key).layers.map((id) => ({ id, title: id })));
      },
    );
    return () => {
      alive = false;
    };
  }, [archive]);

  const streamable = streamOnly && archive?.key !== 'pad.ma';
  const query = useMemo(() => {
    const extra: PandoraCondition[] = streamable
      ? [{ key: 'canplayvideo', operator: '==', value: true }]
      : [];
    return buildQuery(group, extra);
  }, [group, streamable]);

  const facetKeys = useMemo(() => {
    const available = FACET_KEY_CANDIDATES.filter((k) => itemKeys.some((key) => key.id === k));
    return available.length ? available : ['director', 'year', 'language'];
  }, [itemKeys]);
  const activeFacetKey = facetKeys.includes(facetKey) ? facetKey : facetKeys[0];

  const sort: PandoraSort[] = useMemo(
    () => [{ key: sortKey, operator: sortDir }],
    [sortKey, sortDir],
  );

  const runSearch = useCallback(
    async (offset: number) => {
      if (!archive) return;
      const gen = ++generation.current;
      setLoading(true);
      setError(null);
      try {
        if (mode === 'items') {
          if (offset === 0) {
            setCount(null);
            void findItemsCount(archive.site, query).then(
              (n) => gen === generation.current && setCount(n),
              () => undefined,
            );
          }
          const page = await findItemsAdvanced(archive.site, {
            query,
            range: [offset, offset + PAGE],
            sort,
          });
          if (gen !== generation.current) return;
          setItems((prev) => (offset === 0 ? page : [...prev, ...page]));
          setExhausted(page.length < PAGE);
        } else {
          if (!momentQuery.trim() && !layerId) {
            setMoments([]);
            setExhausted(true);
            return;
          }
          const page = await findAnnotations(
            archive.site,
            momentQuery,
            [offset, offset + PAGE],
            layerId || undefined,
          );
          if (gen !== generation.current) return;
          setMoments((prev) => (offset === 0 ? page : [...prev, ...page]));
          setExhausted(page.length < PAGE);
        }
      } catch (e) {
        if (gen !== generation.current) return;
        setError(e instanceof Error ? e.message : 'search failed');
      } finally {
        if (gen === generation.current) setLoading(false);
      }
    },
    [archive, mode, query, sort, momentQuery, layerId],
  );

  // debounce new searches
  useEffect(() => {
    setItems([]);
    setMoments([]);
    setExhausted(false);
    const t = setTimeout(() => void runSearch(0), 350);
    return () => clearTimeout(t);
  }, [runSearch]);

  function markBinned(id: string) {
    setBinnedId(id);
    setTimeout(() => setBinnedId((prev) => (prev === id ? null : prev)), 1200);
  }

  function binItem(item: PandoraItem) {
    if (!archive) return;
    onAddToBin({
      archive: archive.key,
      item: item.id,
      title: item.title || item.id,
      duration: item.duration ?? 0,
      parts: item.parts ?? 1,
    });
    markBinned(item.id);
  }

  async function binMoment(m: PandoraAnnotation) {
    if (!archive) return;
    const itemId = annotationItemId(m.id);
    try {
      const it = await getItem(archive.site, itemId);
      onAddToBin({
        archive: archive.key,
        item: itemId,
        title: it.title || itemId,
        duration: it.duration ?? 0,
        parts: it.parts ?? 1,
        ...(it.posterFrame != null ? { posterFrame: it.posterFrame } : {}),
        inPoint: m.in,
        outPoint: m.out,
      });
      markBinned(m.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'failed to load item');
    }
  }

  function toggleFacetValue(name: string) {
    setGroup((g) => {
      const i = g.rows.findIndex(
        (r) => r.key === activeFacetKey && r.operator === '==' && !r.negate && r.value === name,
      );
      if (i >= 0) return { ...g, rows: g.rows.filter((_, j) => j !== i) };
      return {
        ...g,
        rows: [...g.rows, { key: activeFacetKey, operator: '==', value: name, negate: false }],
      };
    });
  }

  const activeFacetValues = useMemo(
    () =>
      new Set(
        group.rows
          .filter((r) => r.key === activeFacetKey && r.operator === '==' && !r.negate)
          .map((r) => String(r.value)),
      ),
    [group, activeFacetKey],
  );

  if (!archive) return null;

  return (
    <div className="adv-backdrop" onClick={onClose}>
      <div className="adv-overlay" onClick={(e) => e.stopPropagation()}>
        <div className="adv-head">
          <select value={archiveKey} onChange={(e) => setArchiveKey(e.target.value)}>
            {archives.map((a) => (
              <option key={a.key} value={a.key}>
                {a.name}
              </option>
            ))}
          </select>
          <div className="search-modes">
            <button className={mode === 'items' ? 'active' : ''} onClick={() => setMode('items')}>
              Items
            </button>
            <button
              className={mode === 'moments' ? 'active' : ''}
              onClick={() => setMode('moments')}
            >
              Moments
            </button>
          </div>
          {mode === 'items' && (
            <>
              <label className="field adv-sort">
                sort
                <select value={sortKey} onChange={(e) => setSortKey(e.target.value)}>
                  {SORT_KEYS.map((k) => (
                    <option key={k} value={k}>
                      {k}
                    </option>
                  ))}
                </select>
                <select
                  value={sortDir}
                  onChange={(e) => setSortDir(e.target.value as '+' | '-')}
                >
                  <option value="-">desc</option>
                  <option value="+">asc</option>
                </select>
              </label>
              {archive.key !== 'pad.ma' && (
                <label className="field">
                  <input
                    type="checkbox"
                    checked={streamOnly}
                    onChange={(e) => setStreamOnly(e.target.checked)}
                  />
                  streamable only
                </label>
              )}
            </>
          )}
          <span className="adv-count">
            {mode === 'items' && count !== null ? `${count.toLocaleString()} items` : ''}
          </span>
          <button className="ghost icon adv-close" title="Close (Esc)" onClick={onClose}>
            ×
          </button>
        </div>

        <div className={`adv-body ${mode === 'moments' ? 'no-facets' : ''}`}>
          <div className="adv-builder">
            {mode === 'items' ? (
              <GroupEditor
                group={group}
                depth={0}
                site={archive.site}
                keys={itemKeys}
                onChange={setGroup}
              />
            ) : (
              <>
                <input
                  className="adv-moment-query"
                  placeholder="Search transcripts, keywords…"
                  value={momentQuery}
                  onChange={(e) => setMomentQuery(e.target.value)}
                />
                <select value={layerId} onChange={(e) => setLayerId(e.target.value)}>
                  <option value="">all layers</option>
                  {layers.map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.title || l.id}
                    </option>
                  ))}
                </select>
              </>
            )}
          </div>

          <ResultGrid
            archive={archive}
            mode={mode}
            items={items}
            moments={moments}
            loading={loading}
            exhausted={exhausted}
            error={error}
            binnedId={binnedId}
            onLoadMore={() => void runSearch(mode === 'items' ? items.length : moments.length)}
            onOpen={(itemId, preset) => onOpenItem(archive.key, itemId, preset)}
            onBinItem={binItem}
            onBinMoment={(m) => void binMoment(m)}
          />

          {mode === 'items' && (
            <FacetSidebar
              site={archive.site}
              facetKeys={facetKeys}
              facetKey={activeFacetKey}
              onFacetKey={setFacetKey}
              query={query}
              activeValues={activeFacetValues}
              onToggle={toggleFacetValue}
            />
          )}
        </div>
      </div>
    </div>
  );
}

interface GroupEditorProps {
  group: ConditionGroup;
  depth: number; // 0 = top level (may add sub-groups), 1 = nested
  site: string;
  keys: PandoraItemKey[];
  onChange(group: ConditionGroup): void;
  onRemove?: () => void;
}

function GroupEditor({ group, depth, site, keys, onChange, onRemove }: GroupEditorProps) {
  const setOperator = (operator: '&' | '|') => onChange({ ...group, operator });
  return (
    <div className={`adv-group ${depth > 0 ? 'nested' : ''}`}>
      <div className="adv-group-head">
        <div className="search-modes">
          <button className={group.operator === '&' ? 'active' : ''} onClick={() => setOperator('&')}>
            AND
          </button>
          <button className={group.operator === '|' ? 'active' : ''} onClick={() => setOperator('|')}>
            OR
          </button>
        </div>
        {onRemove && (
          <button className="ghost icon" title="Remove group" onClick={onRemove}>
            ×
          </button>
        )}
      </div>

      {group.rows.map((row, i) => (
        <ConditionRowEditor
          key={i}
          row={row}
          keys={keys}
          site={site}
          onChange={(r) =>
            onChange({ ...group, rows: group.rows.map((old, j) => (j === i ? r : old)) })
          }
          onRemove={() => onChange({ ...group, rows: group.rows.filter((_, j) => j !== i) })}
        />
      ))}

      {depth === 0 &&
        group.groups.map((sub, i) => (
          <GroupEditor
            key={i}
            group={sub}
            depth={1}
            site={site}
            keys={keys}
            onChange={(g) =>
              onChange({ ...group, groups: group.groups.map((old, j) => (j === i ? g : old)) })
            }
            onRemove={() =>
              onChange({ ...group, groups: group.groups.filter((_, j) => j !== i) })
            }
          />
        ))}

      <div className="adv-group-actions">
        <button onClick={() => onChange({ ...group, rows: [...group.rows, emptyRow()] })}>
          + condition
        </button>
        {depth === 0 && (
          <button
            onClick={() =>
              onChange({
                ...group,
                groups: [
                  ...group.groups,
                  { ...emptyGroup(group.operator === '&' ? '|' : '&'), rows: [emptyRow()] },
                ],
              })
            }
          >
            + group
          </button>
        )}
      </div>
    </div>
  );
}
